import { Language } from '../types';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeClone<T>(obj: T, maxDepth = 10): T {
  if (obj === null || obj === undefined) return obj;
  const t = typeof obj;
  if (t !== 'object' && t !== 'function') return obj;

  // Ultra-fast native clone path for standard JSON data
  try {
    if (typeof structuredClone !== 'undefined') {
      return structuredClone(obj);
    }
  } catch (_) {}

  const seen = new WeakMap<any, any>();

  function clone(val: any, depth: number): any {
    if (val === undefined || val === null) {
      return null;
    }

    if (depth > maxDepth) {
      return '[Max Depth Reached]';
    }

    const valType = typeof val;
    if (valType !== 'object' && valType !== 'function') {
      return val;
    }

    if (valType === 'function') {
      return '[Function]';
    }

    // Handle circular dependencies using WeakMap
    if (seen.has(val)) {
      return '[Circular]';
    }
    
    // Immediately mark as seen to prevent cycles during toJSON or getters
    try {
      seen.set(val, true);
    } catch (_) {}

    try {
      let isDOMElement = false;
      let tagName = '';
      let constructorName = '';
      try {
        constructorName = val.constructor?.name || '';
      } catch (_) {}

      try {
        isDOMElement = 
          (val.nodeType !== undefined) ||
          (typeof HTMLElement !== 'undefined' && val instanceof HTMLElement) ||
          (typeof Node !== 'undefined' && val instanceof Node) ||
          (constructorName && (
            constructorName.startsWith('HTML') ||
            constructorName.includes('Element') ||
            constructorName.includes('Node')
          )) ||
          (typeof val.tagName === 'string') ||
          (val.ownerDocument !== undefined && val.ownerDocument !== null);
        
        if (typeof val.tagName === 'string') {
          tagName = val.tagName;
        }
      } catch (_) {}

      // Robust detection of Events and minified Google/Firebase system objects (handles native, React SyntheticEvents, Firebase, and Google APIs)
      let isEventOrGoogle = false;
      try {
        if (typeof Event !== 'undefined' && val instanceof Event) {
          isEventOrGoogle = true;
        }
      } catch (_) {}

      if (!isEventOrGoogle) {
        try {
          if (val.preventDefault !== undefined && val.stopPropagation !== undefined) {
            isEventOrGoogle = true;
          }
        } catch (_) {}
      }

      if (!isEventOrGoogle && constructorName) {
        try {
          if (
            constructorName.includes('Event') ||
            constructorName === 'SyntheticBaseEvent' ||
            // Common minified Firebase/Google SDK classes
            constructorName === 'Y' ||
            constructorName === 'Ka' ||
            constructorName === 'La' ||
            constructorName === 'Ia' ||
            constructorName === 'Ja' ||
            constructorName === 'Ma' ||
            constructorName === 'Na' ||
            constructorName === 'Oa' ||
            constructorName === 'Pa' ||
            constructorName === 'Qa' ||
            constructorName === 'Ra' ||
            constructorName === 'Z' ||
            constructorName === 'Ua' ||
            constructorName === 'b' ||
            constructorName === 'se' ||
            constructorName === 'oe' ||
            constructorName === 're'
          ) {
            isEventOrGoogle = true;
          }
        } catch (_) {}
      }

      if (!isEventOrGoogle) {
        try {
          if (
            val._delegate !== undefined ||
            val.firestore !== undefined ||
            val._firestore !== undefined ||
            val._databaseId !== undefined
          ) {
            isEventOrGoogle = true;
          }
        } catch (_) {}
      }

      // Exclude react elements, DOM nodes, events, Window, SVG, and Fibers early with safe string descriptors
      try {
        if (val.$$typeof) {
          return '[ReactElement]';
        }
      } catch (_) {}

      if (isDOMElement) {
        return `[DOM Element: ${constructorName || tagName || 'HTMLElement'}]`;
      }
      if (isEventOrGoogle) {
        return `[Event/Google Object: ${constructorName || 'Event'}]`;
      }

      try {
        if (
          val.window === val ||
          val.self === val ||
          (constructorName && (
            constructorName === 'Window' ||
            constructorName === 'Location'
          ))
        ) {
          return `[System Object: ${constructorName || 'Window'}]`;
        }
      } catch (_) {}

      try {
        if (constructorName && (
          constructorName.startsWith('SVG') || 
          constructorName.includes('Fiber')
        )) {
          return `[System Object: ${constructorName}]`;
        }
      } catch (_) {}

      try {
        if (typeof val.toDate === 'function') {
          return clone(val.toDate(), depth + 1);
        }
      } catch (_) {}

      try {
        if (constructorName === 'GeoPoint' && typeof val.latitude === 'number' && typeof val.longitude === 'number') {
          return { latitude: val.latitude, longitude: val.longitude };
        }
      } catch (_) {}

      try {
        if (
          constructorName && 
          ![
            'Object', 'Array', 'Map', 'Set', 'Date', 'RegExp', 'Error', 
            'Timestamp', 'GeoPoint'
          ].includes(constructorName)
        ) {
          if (typeof val.toJSON === 'function') {
            try {
              const json = val.toJSON();
              // If toJSON returns itself or something circular, clone will catch it
              if (json === val) {
                return `[Custom Object: ${constructorName}]`;
              }
              return clone(json, depth + 1);
            } catch (_) {
              return `[Custom Object: ${constructorName}]`;
            }
          }
          return `[Custom Object: ${constructorName}]`;
        }
      } catch (_) {}

      // Preserve Date, RegExp, and Error objects
      try {
        if (val instanceof Date) {
          return new Date(val.getTime());
        }
      } catch (_) {}

      try {
        if (val instanceof RegExp) {
          return new RegExp(val);
        }
      } catch (_) {}

      try {
        if (val instanceof Error) {
          const errCopy: any = {
            name: val.name,
            message: val.message,
            stack: val.stack
          };
          try {
            seen.set(val, errCopy);
          } catch (_) {}
          for (const key in val) {
            if (!Object.prototype.hasOwnProperty.call(val, key)) {
              continue;
            }
            if (typeof key === 'string' && !['name', 'message', 'stack'].includes(key)) {
              if (
                key.startsWith('__react') ||
                key.startsWith('_react') ||
                key.startsWith('__key') ||
                [
                  'stateNode', '_owner', '_context', 'updater', 'child', 'sibling', 
                  'return', 'alternate', 'memoizedState', 'memoizedProps', 'pendingProps', 
                  '_reactInternals', '_reactInternalFiber', 'target', 'currentTarget', 
                  'srcElement', 'ownerDocument', 'parentNode', 'parentElement', 'children', 
                  'nextSibling', 'previousSibling', 'src'
                ].includes(key)
              ) {
                continue;
              }
              try {
                const propValue = (val as any)[key];
                const clonedProp = clone(propValue, depth + 1);
                if (clonedProp !== undefined) {
                  errCopy[key] = clonedProp;
                }
              } catch (e) {}
            }
          }
          return errCopy;
        }
      } catch (_) {}

      // Handle Map
      try {
        if (val instanceof Map) {
          const mapCopy = new Map();
          try {
            seen.set(val, mapCopy);
          } catch (_) {}
          for (const [k, v] of val.entries()) {
            const clonedKey = clone(k, depth + 1);
            const clonedVal = clone(v, depth + 1);
            if (clonedKey !== undefined && clonedVal !== undefined) {
              mapCopy.set(clonedKey, clonedVal);
            }
          }
          return mapCopy;
        }
      } catch (_) {}

      // Handle Set
      try {
        if (val instanceof Set) {
          const setCopy = new Set();
          try {
            seen.set(val, setCopy);
          } catch (_) {}
          for (const item of val) {
            const clonedItem = clone(item, depth + 1);
            if (clonedItem !== undefined) {
              setCopy.add(clonedItem);
            }
          }
          return setCopy;
        }
      } catch (_) {}

      // Handle Arrays
      if (Array.isArray(val)) {
        const arrCopy: any[] = [];
        try {
          seen.set(val, arrCopy);
        } catch (_) {}
        for (let i = 0; i < val.length; i++) {
          const itemCopy = clone(val[i], depth + 1);
          arrCopy.push(itemCopy !== undefined ? itemCopy : null);
        }
        return arrCopy;
      }

      // Handle Plain and custom Objects
      const objCopy: any = {};
      try {
        seen.set(val, objCopy);
      } catch (_) {}

      for (const key in val) {
        if (!Object.prototype.hasOwnProperty.call(val, key)) {
          continue;
        }
        // Filter out React internal keys or cyclic framework structures from serialization
        if (
          typeof key === 'string' && (
            key.startsWith('__react') ||
            key.startsWith('_react') ||
            key.startsWith('__key') ||
            key === 'stateNode' ||
            key === '_owner' ||
            key === '_context' ||
            key === 'updater' ||
            key === 'child' ||
            key === 'sibling' ||
            key === 'return' ||
            key === 'alternate' ||
            key === 'memoizedState' ||
            key === 'memoizedProps' ||
            key === 'pendingProps' ||
            key === '_reactInternals' ||
            key === '_reactInternalFiber' ||
            key === 'target' ||
            key === 'currentTarget' ||
            key === 'srcElement' ||
            key === 'ownerDocument' ||
            key === 'parentNode' ||
            key === 'parentElement' ||
            key === 'children' ||
            key === 'nextSibling' ||
            key === 'previousSibling' ||
            key === 'src'
          )
        ) {
          continue;
        }

        try {
          const propValue = val[key];
          const clonedProp = clone(propValue, depth + 1);
          if (clonedProp !== undefined) {
            objCopy[key] = clonedProp;
          }
        } catch (err) {
          // Skip properties that throw exceptions on read
        }
      }

      return objCopy;
    } catch (e) {
      return '[Unclonable Object]';
    }
  }

  try {
    return clone(obj, 0);
  } catch (e) {
    console.warn("safeClone recursive fallback:", e);
    return (Array.isArray(obj) ? [] : {}) as T;
  }
}

/**
 * Robust JSON stringification that handles circular references and system objects
 */
export function safeStringify(obj: any, indent?: number): string {
  try {
    return JSON.stringify(obj, null, indent);
  } catch (_) {
    try {
      return JSON.stringify(safeClone(obj), null, indent);
    } catch (e) {
      console.error("Critical: safeStringify failed even with safeClone:", e);
      return "[Unstringifiable Object]";
    }
  }
}


export function ensureTimestampMillis(timestamp: any): number {
  if (!timestamp) return Date.now();
  if (typeof timestamp === 'number') return timestamp;
  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    return isNaN(parsed) ? Date.now() : parsed;
  }
  if (typeof timestamp === 'object') {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
    if (typeof timestamp.seconds === 'number') {
      return timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1000000);
    }
    if (typeof timestamp._seconds === 'number') {
      return timestamp._seconds * 1000 + Math.floor((timestamp._nanoseconds || 0) / 1000000);
    }
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }
  }
  return Date.now();
}

export function formatDate(date: Date, format: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY', lang: Language = 'en-US', shortYear: boolean = false): string {
  const day = String(date.getDate()).padStart(2, '0');
  const dayNum = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const fullYear = date.getFullYear();
  const year = shortYear ? String(fullYear).slice(-2) : fullYear;

  switch (format) {
    case 'D MMMM YYYY': {
      const monthName = date.toLocaleDateString(lang.startsWith('ua') ? 'uk-UA' : 'en-US', { month: 'long' });
      return `${dayNum} ${monthName} ${year}`;
    }
    case 'DD/MM/YYYY':
      return `${day}.${month}.${year}`;
    case 'YYYY-MM-DD':
      return shortYear ? `${year}-${month}-${day}` : `${fullYear}-${month}-${day}`;
    case 'MM/DD/YYYY':
    default:
      return `${month}.${day}.${year}`;
  }
}

export function formatDateTime(date: Date, dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY', timeFormat: '12h' | '24h', lang: Language, shortYear: boolean = false): string {
  const dateStr = formatDate(date, dateFormat, lang, shortYear);
  const timeStr = date.toLocaleTimeString(lang.startsWith('ua') ? 'uk-UA' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' });
  return `${dateStr} ${timeStr}`;
}

export function sanitizeChartData(data: any[]): any[] {
  if (!Array.isArray(data)) return [];
  return data.map(item => {
    if (!item || typeof item !== 'object') return item;
    const cleanItem: any = {};
    for (const key in item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        const val = item[key];
        const t = typeof val;
        if (t === 'string' || t === 'number' || t === 'boolean' || val === null) {
          cleanItem[key] = val;
        }
      }
    }
    return cleanItem;
  });
}

/**
 * Returns the YYYY-MM-DD date string corresponding to the current evening wrap-up session.
 * - For hours >= 4 (4:00 AM onwards): returns today's local date YYYY-MM-DD.
 * - For hours < 4 (midnight to 3:59 AM): returns yesterday's local date YYYY-MM-DD,
 *   as late night reading belongs to the preceding evening session.
 */
export function getEveningWrapUpSessionDate(d: Date = new Date()): string {
  const dateObj = new Date(d);
  if (dateObj.getHours() < 4) {
    dateObj.setDate(dateObj.getDate() - 1);
  }
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns the YYYY-MM-DD date string for the day preceding the given sessionDateStr.
 */
export function getPreviousSessionDate(sessionDateStr: string): string {
  const [year, month, day] = sessionDateStr.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  dateObj.setDate(dateObj.getDate() - 1);
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface EveningWrapUpState {
  shouldShow: boolean;
  sessionDate: string;
  isTransferred: boolean;
  isDismissed: boolean;
}

/**
 * Evaluates whether Evening Wrap-Up should be displayed right now and which session it belongs to.
 * Rule: Evening wrap up is ONLY transferred to the next day if it wasn't opened or removed on its original day.
 */
export function getEveningWrapUpStatus(isEveningOverride: boolean = false): EveningWrapUpState {
  const now = new Date();
  const currentSessionDate = getEveningWrapUpSessionDate(now);

  if (isEveningOverride) {
    return {
      shouldShow: true,
      sessionDate: currentSessionDate,
      isTransferred: false,
      isDismissed: false
    };
  }

  const currentHour = now.getHours();
  const is11pmEvening = currentHour >= 23 || currentHour < 4;

  if (is11pmEvening) {
    // Current evening window (11 PM to 4 AM)
    const isDismissed = typeof localStorage !== 'undefined' && localStorage.getItem(`evening_wrapup_dismissed_${currentSessionDate}`) === 'true';
    return {
      shouldShow: !isDismissed,
      sessionDate: currentSessionDate,
      isTransferred: false,
      isDismissed
    };
  } else {
    // Daytime window (4 AM to 11 PM)
    // Check if yesterday's evening wrap-up should be transferred to today.
    const yesterdaySessionDate = getPreviousSessionDate(currentSessionDate);

    let wasYesterdayOpened = false;
    let wasYesterdayDismissed = false;

    if (typeof localStorage !== 'undefined') {
      wasYesterdayOpened = localStorage.getItem(`evening_wrapup_opened_${yesterdaySessionDate}`) === 'true';
      wasYesterdayDismissed = localStorage.getItem(`evening_wrapup_dismissed_${yesterdaySessionDate}`) === 'true';
    }

    // Transfer rule: Only transfer to the next day if it was NOT opened AND NOT removed (dismissed) on its original day.
    if (!wasYesterdayOpened && !wasYesterdayDismissed) {
      return {
        shouldShow: true,
        sessionDate: yesterdaySessionDate,
        isTransferred: true,
        isDismissed: false
      };
    } else {
      return {
        shouldShow: false,
        sessionDate: currentSessionDate,
        isTransferred: false,
        isDismissed: wasYesterdayDismissed || wasYesterdayOpened
      };
    }
  }
}

export function safeJsonParse<T>(input: string | null | undefined, fallback: T): T {
  if (!input || input === 'undefined' || input === 'null') {
    return fallback;
  }
  try {
    const parsed = JSON.parse(input);
    return (parsed === null || parsed === undefined) ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

export function isValidMealId(id: any): id is string {
  return typeof id === 'string' && id.trim() !== '' && id.trim() !== 'temp';
}

export function cleanInsightQuotes(text: string | null | undefined): string {
  if (!text) return '';
  let cleaned = String(text).trim();
  cleaned = cleaned.replace(/^[\s"'«»“”„]+|[\s"'«»“”„]+$/g, '').trim();
  if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  return cleaned;
}

